// How a trip's price is split. Pure arithmetic, shared by the checkout edge
// function and the admin Pricing preview so both always agree.
//
// The traveler pays `grossCents`, which includes Sekretly's VAT. The host fee
// is a percentage of the price WITHOUT VAT (any VAT the host charges on their
// own invoice is inside that fee). Sekretly keeps the rest.
//
//   97.00 at 23% VAT and a 65% host share:
//   net 78.86, VAT 18.14, host fee 51.26, Sekretly keeps 27.60.
//
// Rounding: the price without VAT is rounded to the cent first, the host fee
// is worked out from it, and VAT and Sekretly's share are what is left, so the
// parts always add up exactly.

export interface TripPricing {
  /** What the traveler pays, VAT included. */
  grossCents: number;
  /** The price without VAT. */
  netCents: number;
  vatRate: number;
  vatCents: number;
  hostShareRate: number;
  /** What the host is paid for the trip (the total on their invoice). */
  hostFeeCents: number;
  /** What Sekretly keeps from the price without VAT. */
  commissionCents: number;
  /** Sekretly's share of the price without VAT, in percent. */
  commissionRate: number;
}

function wholePercent(name: string, value: number): void {
  if (!Number.isInteger(value) || value < 0 || value > 100) {
    throw new RangeError(`${name} must be a whole number from 0 to 100`);
  }
}

export function computeTripPricing(grossCents: number, vatPercent: number, hostSharePercent: number): TripPricing {
  if (!Number.isInteger(grossCents) || grossCents < 0) {
    throw new RangeError('grossCents must be a whole number of cents, zero or more');
  }
  wholePercent('vatPercent', vatPercent);
  wholePercent('hostSharePercent', hostSharePercent);

  const netCents = Math.round(grossCents / (1 + vatPercent / 100));
  const vatCents = grossCents - netCents;
  const hostFeeCents = Math.round((netCents * hostSharePercent) / 100);
  return {
    grossCents,
    netCents,
    vatRate: vatPercent,
    vatCents,
    hostShareRate: hostSharePercent,
    hostFeeCents,
    commissionCents: netCents - hostFeeCents,
    commissionRate: 100 - hostSharePercent,
  };
}

// ── What a trip costs ─────────────────────────────────────────────────────────
//
// A single stay costs the base price for its first day plus the extra-day price
// for every other day. A trip with several cities (legs) prices each city that
// way on its own: the first day of every city costs the base price. Days
// between two cities (a transit day) belong to no city and are not charged.
//
//   base 47.00, extra 10.00:  Porto 3 days + Lisbon 4 days
//   = (47 + 2 × 10) + (47 + 3 × 10) = 144.00, against 47 + 6 × 10 = 107.00 as
//   one 7-day stay.
//
// The checkout function and the traveler's payment page both call quoteTrip, so
// the price shown is always the price charged.

export interface TripQuote {
  /** What the traveler pays, VAT included. */
  grossCents: number;
  /** Paid days: the sum of the cities' days when priced per city. */
  tripDays: number;
  /** One entry per city, in trip order. Empty when the trip is priced as one stay. */
  legs: { days: number; grossCents: number }[];
}

export interface TripQuoteInput {
  /** platform_settings.per_city_trips. Off: the trip is priced as one stay (of the days in its cities). */
  perCity: boolean;
  /** The trip's cities, in order (empty for a trip without any). */
  legs: { days: number }[];
  /** Days of the whole trip, used for a trip without cities. */
  tripDays: number;
  baseCents: number;
  extraCents: number;
}

export function quoteTrip(input: TripQuoteInput): TripQuote {
  const { perCity, legs, tripDays, baseCents, extraCents } = input;
  if (!Number.isInteger(baseCents) || baseCents < 0 || !Number.isInteger(extraCents) || extraCents < 0) {
    throw new RangeError('prices must be whole numbers of cents, zero or more');
  }

  for (const leg of legs) {
    if (!Number.isInteger(leg.days) || leg.days < 1) {
      throw new RangeError('every city needs a whole number of days, at least 1');
    }
  }

  if (!perCity || legs.length === 0) {
    // One stay. A trip with cities pays for the days in them, so a day left out
    // between two cities is not charged; a trip without cities pays for its days.
    const paidDays = legs.length > 0 ? legs.reduce((sum, l) => sum + l.days, 0) : tripDays;
    return {
      grossCents: baseCents + Math.max(0, paidDays - 1) * extraCents,
      tripDays: paidDays,
      legs: [],
    };
  }

  const priced = legs.map((leg) => ({ days: leg.days, grossCents: baseCents + (leg.days - 1) * extraCents }));
  return {
    grossCents: priced.reduce((sum, l) => sum + l.grossCents, 0),
    tripDays: priced.reduce((sum, l) => sum + l.days, 0),
    legs: priced,
  };
}
